import { ReactNode, useState } from "react";

import { useBlacklistOverrideGate } from "@/components/BlacklistOverride/useBlacklistOverrideGate";

import { ListPerson } from "../../../schema/list";
import { isLeadBlacklisted, isLeadUnsubscribed } from "../../../utils/isLeadBlacklisted";

interface UseLeadPanelReachOutParams {
	/** The lead the panel shows (the slice's live copy when there is one). */
	lead?: ListPerson | null;
	/** The surface has a per-lead Chat view of its own (not a Messages rail, and a lead to chat to). */
	chatAvailable: boolean;
	/** Switch the panel to its Chat view. */
	openChat: () => void;
	/** Where reach-out goes on a host without a Chat view of its own (the Outbox's Chat tab). */
	onHostReachOut?: () => void;
}

interface LeadPanelReachOut {
	/** The lead is blacklisted and nobody has confirmed contacting it: no chat, and the banner. */
	isBlockedLead: boolean;
	/** Offer the panel's Lead Panel | Chat toggle. */
	showChat: boolean;
	/** The reach-out button's action; undefined when the panel offers none (never a dead button). */
	reachOut?: () => void;
	/** The "contact this blacklisted lead?" prompt; render it inside the panel. */
	overrideModal: ReactNode;
}

/**
 * Where the lead panel's "Reach out" goes (PREC-2548).
 *
 * A blacklisted lead used to have no Reach out at all (PREC-3728). It now gets one behind the
 * blacklist gate: the click asks first, takes the lead off the blacklist on confirm, and then goes
 * where it would for any lead — the panel's Chat view, or the host's. An unsubscribed lead keeps
 * no button: the gate could only ever refuse, and a control that can never work is left out
 * (web rule §19).
 */
export const useLeadPanelReachOut = ({
	lead,
	chatAvailable,
	openChat,
	onHostReachOut,
}: UseLeadPanelReachOutParams): LeadPanelReachOut => {
	const { gate, overrideModal } = useBlacklistOverrideGate();
	// The lead the user just took off the blacklist. The row still reads BLACKLIST until the table
	// refetches, so the decision is remembered — with the id it was made for, so switching to
	// another lead never lets that one through (web rule §23).
	const [overriddenLeadId, setOverriddenLeadId] = useState<number | null>(null);

	const isBlockedLead = !!lead && isLeadBlacklisted(lead) && overriddenLeadId !== lead.id;
	const destination = chatAvailable ? openChat : onHostReachOut;

	// A blocked lead (never an unsubscribed one, which gets no button) goes through the gate first.
	const resolveReachOut = (): (() => void) | undefined => {
		if (!destination || isLeadUnsubscribed(lead)) {
			return undefined;
		}

		if (!isBlockedLead || !lead) {
			return destination;
		}

		return () => {
			void gate([lead], () => {
				setOverriddenLeadId(lead.id);
				destination();
			});
		};
	};

	return {
		isBlockedLead,
		showChat: chatAvailable && !isBlockedLead,
		reachOut: resolveReachOut(),
		overrideModal,
	};
};
